import { fetchGraphQL } from 'libs/contentful/contentful';
import { appendFragmentsToQuery } from 'libs/contentful/fragmentUtils';
import { isDraftPreviewEnabled } from './draftPreview';
import {
  headerCtaFragment,
  heroCarouselFragment,
  heroConsolidatedFragment,
  homepageHeroFragment,
  mainNavFragment,
  footerFragment,
  disclosureGroupingsFragment,
  disclosureBannerFragment,
  featuredBannerFragment,
  headerLogoFragment,
} from 'libs/contentful/fragments';
import {
  HeroConsolidatedProps,
  HeroCarouselProps,
  HomepageHeroProps,
  MainNavProps,
  HeaderCtaProps,
  FooterProps,
  DisclosureGroupingsProps,
  DisclosureBannerProps,
  FeaturedBannerProps,
  HeaderLogoProps,
} from 'libs/contentful/props';
import { ModuleTypeName, ModuleTypes } from './modules';
import { NinetailedContentfulModule } from 'libs/contentful/queries/ninetailed';

export const SECTION_MODULE_TYPES = [
  'SectionHero',
  'SectionNavigation',
  'SectionFooter',
  'SectionDisclosures',
  'PageSection',
  'SectionBanner',
] as const;

export type SectionModuleType = (typeof SECTION_MODULE_TYPES)[number];

type SectionModuleConfig = {
  [K in SectionModuleType]: {
    fetchFunction: (id: string) => Promise<Record<string, unknown> | null>;
    dataProperty: string;
    variantDataPath?: string; // Path to extract variant data from for disclosure extraction
    variantExtractNestedModule?: boolean; // If true, extract from nested module at variantDataPath; if false, pass entire variant with __typename
  };
};

export const SECTION_MODULE_CONFIG: SectionModuleConfig = {
  SectionHero: {
    fetchFunction: getSectionHeroData,
    dataProperty: 'hero',
    variantDataPath: 'hero', // For variants, extract disclosures from the 'hero' property
    variantExtractNestedModule: true, // The 'hero' property is a complete module (e.g., HeroConsolidated)
  },
  SectionDisclosures: {
    fetchFunction: getSectionDisclosuresData,
    dataProperty: 'disclosuresCollection',
  },
  SectionFooter: {
    fetchFunction: getSectionFooterData,
    dataProperty: 'footer',
  },
  SectionNavigation: {
    fetchFunction: getSectionNavigationData,
    dataProperty: 'nav',
  },
  PageSection: {
    fetchFunction: async () => null, // PageSection doesn't need separate fetching
    dataProperty: 'modulesCollection',
    variantDataPath: 'modulesCollection', // For variants, extract disclosures from the 'modulesCollection' property
    variantExtractNestedModule: false, // The 'modulesCollection' is a property, not a complete module
  },
  SectionBanner: {
    fetchFunction: getSectionBannerData,
    dataProperty: 'headerBannerCollection',
  },
};

export const isSectionModule = (moduleName: string): moduleName is SectionModuleType => {
  return Object.keys(SECTION_MODULE_CONFIG).includes(moduleName);
};

/**
 * Generic function to process section modules and fetch their detailed data
 * This avoids query complexity by fetching section data separately
 */
export const processSectionModule = async (
  module: ModuleTypes[ModuleTypeName],
  sectionType: SectionModuleType,
  fetchFunction: (id: string) => Promise<Record<string, unknown> | null>,
  dataProperty: string,
) => {
  if (module?.__typename === sectionType && module.sys?.id) {
    try {
      const sectionDetails = await fetchFunction(module.sys.id);
      if (sectionDetails?.[dataProperty]) {
        // Merge the detailed data into the module
        (module as unknown as Record<string, unknown>)[dataProperty] = sectionDetails[dataProperty];
      } else {
        console.warn(`processSectionModule: ${dataProperty} is missing in sectionDetails for ${sectionType}`);
      }
    } catch (error) {
      console.error(`Failed to fetch ${sectionType} details:`, error);
    }
  }
};

/**
 * Generic function to process all section modules in a collection
 */
export const processAllSectionModules = async (modules: ModuleTypes[ModuleTypeName][]) => {
  const promises = modules.flatMap(module =>
    Object.entries(SECTION_MODULE_CONFIG).map(([sectionType, config]) =>
      processSectionModule(module, sectionType as SectionModuleType, config.fetchFunction, config.dataProperty),
    ),
  );

  return Promise.all(promises);
};

/**
 * Generic function to process section module experiences for Ninetailed
 */
type NinetailedComponent = {
  type: string;
  variants?: NinetailedContentfulModule[];
  [key: string]: unknown;
};

type IncompleteModule = {
  __typename: string;
  sys: { id: string };
};

type ModuleCollection<T = IncompleteModule> = {
  items: T[];
};

type SectionModuleData = IncompleteModule | ModuleCollection<IncompleteModule>;

const isIncompleteModule = (module: SectionModuleData) => {
  if (!module || typeof module !== 'object') {
    return false;
  }

  // Check if this is a collection with items (like disclosuresCollection)
  if ('items' in module) {
    const items = module.items;

    if (!Array.isArray(items) || items.length === 0) {
      return false;
    }

    // Check if the first item in the collection is incomplete
    const firstItem = items[0];
    return hasIncompleteFields(firstItem);
  }

  // Handle the standard case (single module)
  return hasIncompleteFields(module);
};

const hasIncompleteFields = (item: unknown) => {
  if (!item || typeof item !== 'object') {
    return false;
  }

  const obj = item as Record<string, unknown>;
  const keys = Object.keys(obj);

  // Check for required fields
  const hasTypename = typeof obj.__typename === 'string';
  const hasSysId = obj.sys !== null && typeof obj.sys === 'object' && 'id' in obj.sys;

  // Incomplete modules should only have __typename and sys.id
  return Boolean(hasTypename && hasSysId && keys.length <= 2);
};

export const processSectionModuleExperiences = async <T extends Record<string, unknown>>(
  mappedExperiences: T[],
  sectionType: SectionModuleType,
  fetchFunction: (id: string) => Promise<Record<string, unknown> | null>,
  dataProperty: string,
) => {
  return Promise.all(
    mappedExperiences.map(async experience => {
      if (!experience?.components) return experience;

      const mergedComponents = await Promise.all(
        (experience.components as NinetailedComponent[]).map(async component => {
          if (!component.variants) return component;

          const mergedVariants = await Promise.all(
            component.variants.map(async variant => {
              if (!(dataProperty in variant && variant.id)) return variant;

              const contentfulVariant = variant as NinetailedContentfulModule & Record<string, unknown>;
              const dataPropertyValue = contentfulVariant[dataProperty] as IncompleteModule;

              if (isIncompleteModule(dataPropertyValue)) {
                try {
                  const sectionData = await fetchFunction(variant.id);
                  if (sectionData?.[dataProperty]) {
                    contentfulVariant[dataProperty] = sectionData[dataProperty];
                  }
                } catch (error) {
                  console.error(`Failed to fetch ${sectionType} details for variant:`, error);
                }
              }
              return variant;
            }),
          );

          return { ...component, variants: mergedVariants };
        }),
      );

      return { ...experience, components: mergedComponents };
    }),
  );
};

/**
 * Fetches full SectionHero data for a given SectionHero ID
 * This is used to avoid query complexity by fetching SectionHero data separately
 */
type SectionHeroResponse = {
  __typename: string;
  hero: HeroConsolidatedProps | HeroCarouselProps | HomepageHeroProps;
};

export async function getSectionHeroData(sectionHeroId: string): Promise<SectionHeroResponse | null> {
  const draftPreviewEnabled = await isDraftPreviewEnabled();

  const query = appendFragmentsToQuery(`
    query GetSectionHero {
      sectionHero(id: "${sectionHeroId}", preview: ${draftPreviewEnabled}) {
        __typename
        ...SysId
        hero {
          __typename
          ... on HeroConsolidated {
            ${heroConsolidatedFragment}
          }
          ... on HeroCarousel {
            ${heroCarouselFragment}
          }
          ... on HomepageHero {
            ${homepageHeroFragment}
          }
        }
      }
    }
  `);

  const response = await fetchGraphQL<{ data?: { sectionHero?: SectionHeroResponse } }>(query, draftPreviewEnabled);
  return response?.data?.sectionHero ?? null;
}

/**
 * Fetches full SectionNavigation data for a given SectionNavigation ID
 * This is used to avoid query complexity by fetching SectionNavigation data separately
 */
type SectionNavigationResponse = {
  __typename: string;
  nav: MainNavProps | HeaderCtaProps;
};

export async function getSectionNavigationData(sectionNavigationId: string): Promise<SectionNavigationResponse | null> {
  const draftPreviewEnabled = await isDraftPreviewEnabled();

  const query = appendFragmentsToQuery(`
    query GetSectionNavigation {
      sectionNavigation(id: "${sectionNavigationId}", preview: ${draftPreviewEnabled}) {
        __typename
        ...SysId
        nav {
          __typename
          ... on Nav {
            ${mainNavFragment}
          }
          ... on HeaderCta {
            ${headerCtaFragment}
          }
        }
      }
    }
  `);

  const response = await fetchGraphQL<{ data?: { sectionNavigation?: SectionNavigationResponse } }>(
    query,
    draftPreviewEnabled,
  );
  return response?.data?.sectionNavigation ?? null;
}

/**
 * Fetches full SectionFooter data for a given SectionFooter ID
 * This is used to avoid query complexity by fetching SectionFooter data separately
 */
type SectionFooterResponse = {
  __typename: string;
  footer: FooterProps;
};

export async function getSectionFooterData(sectionFooterId: string): Promise<SectionFooterResponse | null> {
  const draftPreviewEnabled = await isDraftPreviewEnabled();

  const query = appendFragmentsToQuery(`
    query GetSectionFooter {
      sectionFooter(id: "${sectionFooterId}", preview: ${draftPreviewEnabled}) {
        __typename
        ...SysId
        footer {
          __typename
          ... on SiteFooter {
            ${footerFragment}
          }
        }
      }
    }
  `);

  const response = await fetchGraphQL<{ data?: { sectionFooter?: SectionFooterResponse } }>(query, draftPreviewEnabled);
  return response?.data?.sectionFooter ?? null;
}

/**
 * Fetches full SectionDisclosures data for a given SectionDisclosures ID
 * This is used to avoid query complexity by fetching SectionDisclosures data separately
 */
type SectionDisclosuresResponse = {
  __typename: string;
  disclosuresCollection: {
    items: (DisclosureGroupingsProps | DisclosureBannerProps)[];
  };
};

export async function getSectionDisclosuresData(
  sectionDisclosuresId: string,
): Promise<SectionDisclosuresResponse | null> {
  const draftPreviewEnabled = await isDraftPreviewEnabled();

  const query = appendFragmentsToQuery(`
    query GetSectionDisclosures {
      sectionDisclosures(id: "${sectionDisclosuresId}", preview: ${draftPreviewEnabled}) {
        __typename
        ...SysId
        disclosuresCollection(limit: 5) {
          items {
            __typename
            ... on DisclosureGroupings {
              ${disclosureGroupingsFragment}
            }
            ... on DisclosureBanner {
              ${disclosureBannerFragment}
            }
          }
        }
      }
    }
  `);

  const response = await fetchGraphQL<{ data?: { sectionDisclosures?: SectionDisclosuresResponse } }>(
    query,
    draftPreviewEnabled,
  );
  return response?.data?.sectionDisclosures ?? null;
}

/**
 * Fetches full SectionBanner data for a given SectionBanner ID
 * This is used to avoid query complexity by fetching SectionBanner data separately
 */
type SectionBannerResponse = {
  __typename: string;
  headerBannerCollection: {
    items: (FeaturedBannerProps | HeaderLogoProps)[];
  };
};

export async function getSectionBannerData(sectionBannerId: string): Promise<SectionBannerResponse | null> {
  const draftPreviewEnabled = await isDraftPreviewEnabled();

  const query = appendFragmentsToQuery(`
    query GetSectionBanner {
      sectionBanner(id: "${sectionBannerId}", preview: ${draftPreviewEnabled}) {
        __typename
        ...SysId
        headerBannerCollection {
          items {
            __typename
            ... on FeaturedBanner {
              ${featuredBannerFragment}
            }
            ... on HeaderLogo {
              ${headerLogoFragment}
            }
          }
        }
      }
    }
  `);

  const response = await fetchGraphQL<{ data?: { sectionBanner?: SectionBannerResponse } }>(query, draftPreviewEnabled);
  return response?.data?.sectionBanner ?? null;
}
